import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button, Chip, Pagination, Spinner, Table } from "@heroui/react";
import { Icon } from "@iconify/react";

import { AdminOrderEntity } from "@/features/order/types.ts";
import {
  ADMIN_ORDERS_PAGE_SIZE,
  useOrders,
} from "@/features/order/use-orders.ts";
import { orderStatusColor } from "@/shared/helper/order-status.ts";
import { orderTools } from "@/shared/helper/order.ts";
import {
  PAGINATION_ELLIPSIS,
  paginationRange,
} from "@/shared/helper/pagination.ts";
import { formatDateTime } from "@/shared/helper/datetime.ts";

export function UserOrders({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  const { data, isLoading } = useOrders({
    userId,
    page,
    limit: ADMIN_ORDERS_PAGE_SIZE,
  });

  const orders = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / ADMIN_ORDERS_PAGE_SIZE);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center gap-2">
        <p className="font-bold text-lg">Заказы</p>
        <span className="text-sm text-default-500">{total}</span>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <Table.Root>
          <Table.Content aria-label="Заказы пользователя">
            <Table.Header>
              <Table.Column>ID</Table.Column>
              <Table.Column>Создан</Table.Column>
              <Table.Column>Маршрут</Table.Column>
              <Table.Column>Мест</Table.Column>
              <Table.Column>Статус</Table.Column>
              <Table.Column> </Table.Column>
            </Table.Header>
            <Table.Body items={orders} renderEmptyState={() => "Заказов нет"}>
              {(order: AdminOrderEntity) => (
                <Table.Row id={String(order.id)}>
                  <Table.Cell className="font-mono text-sm">
                    {orderTools.formatOrderId(order.id)}
                  </Table.Cell>
                  <Table.Cell className="text-sm text-default-500">
                    {formatDateTime(order.createdAt)}
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    {order.fromCity?.name} → {order.toCity?.name}
                  </Table.Cell>
                  <Table.Cell className="text-sm">
                    {order.packages?.length ?? 0}
                  </Table.Cell>
                  <Table.Cell>
                    <Chip
                      color={orderStatusColor(order.status)}
                      size="sm"
                      variant="soft"
                    >
                      {orderTools.status.toString(order.status)}
                    </Chip>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex justify-end">
                      <Button
                        isIconOnly
                        variant="ghost"
                        onPress={() =>
                          navigate({
                            to: "/dashboard/order/$orderId",
                            params: { orderId: String(order.id) },
                          })
                        }
                      >
                        <Icon
                          className="size-5"
                          icon="solar:arrow-right-outline"
                        />
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Content>
        </Table.Root>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination>
            <Pagination.Content>
              <Pagination.Item>
                <Pagination.Previous
                  isDisabled={page <= 1}
                  onPress={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Назад
                </Pagination.Previous>
              </Pagination.Item>
              {paginationRange(page, totalPages).map((slot, index) => (
                <Pagination.Item key={`${slot}-${index}`}>
                  {slot === PAGINATION_ELLIPSIS ? (
                    <Pagination.Ellipsis />
                  ) : (
                    <Pagination.Link
                      isActive={page === slot}
                      onPress={() => setPage(slot)}
                    >
                      {slot}
                    </Pagination.Link>
                  )}
                </Pagination.Item>
              ))}
              <Pagination.Item>
                <Pagination.Next
                  isDisabled={page >= totalPages}
                  onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Вперёд
                </Pagination.Next>
              </Pagination.Item>
            </Pagination.Content>
          </Pagination>
        </div>
      )}
    </div>
  );
}
